<script lang="ts" setup>
	import { computed, ref, watch } from 'vue';
	import { useModalStore } from '@/store/modal.store';
	import CopyButton from '@/components/block/CopyButton.vue';
	import { useMarketingStore } from '@/store/marketing.store';
	import DefaultBtn from "@/components/block/DefaultBtn.vue";
	import { ISignUpLink } from '@/types';
	import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
	import * as XLSX from 'xlsx';
	import { useNotificationsStore } from '@/store/notifications.store';

	const props = defineProps<{
		closeSignal?: boolean
	}>();

	watch(() => props.closeSignal, () => {
		if (props.closeSignal) {
			close();
		}
	});

	const modalStore = useModalStore();
	const notificationsStore = useNotificationsStore();

	const loading = ref<boolean>(false);
	const fileError = ref<string>("");
	const nameError = ref<string>("");
	const fileInput = ref<HTMLInputElement | null>(null);

	function xlsxToCsv(xlsxFile: File): Promise<File> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onload = function (e: ProgressEvent<FileReader>) {
				if (!e.target || !e.target.result) {
					reject(new Error('Error reading file'));
					return;
				}

				const data = new Uint8Array(e.target.result as ArrayBuffer);
				const workbook = XLSX.read(data, { type: 'array' });
				const sheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[sheetName];
				const csv = XLSX.utils.sheet_to_csv(worksheet);

				const blob = new Blob([csv], { type: 'text/csv' });
				const csvFile = new File([blob], 'converted.csv', { type: 'text/csv' });
				resolve(csvFile);
			};

			reader.onerror = function (error) {
				reject(error);
			};

			reader.readAsArrayBuffer(xlsxFile);
		});
	}

	function isXlsxFile(file: File): boolean {
		const xlsxMimeTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'application/vnd.ms-excel.sheet.macroEnabled.12',
			'application/vnd.ms-excel',
			'application/octet-stream'
		];
		return xlsxMimeTypes.includes(file.type);
	}

	function isCsvFile(file: File): boolean {
		const csvMimeTypes = [
			'text/csv',
			'application/csv',
			'text/plain'
		];

		const csvExtensions = [
			'.csv'
		];

		const fileTypeMatches = csvMimeTypes.includes(file.type);
		const fileNameMatches = csvExtensions.some(extension => file.name.endsWith(extension));

		return fileTypeMatches || fileNameMatches;
	}

	function handleFileChange(event: Event) {
		const input = event.target as HTMLInputElement;
		const selectedFile = input.files?.[0] || null;
		input.value = '';

		if (selectedFile) {

			if (isXlsxFile(selectedFile)) {
				fileLoading.value = true;
				xlsxToCsv(selectedFile)
					.then((result) => {
						uploadFile(result);
					})
					.catch(err => {
						console.error(err);
						notificationsStore.error('Upload File Failed');
					})
					.finally(() => {
						fileLoading.value = true;
					})
			} else if (isCsvFile(selectedFile)) {
				uploadFile(selectedFile);
			}
		}
	}

	function handleFileUpload() {
		fileInput.value?.click();
	}

	function download() {
		modalStore.openModal('MarketingNewLinkDownload');

	}

	function upload() {
		const link = document.createElement('a');
		link.href = 'https://app.floodalert360.com/img/FA-customer-list-template.xlsx'; 
    link.download = 'FA customer list template.xlsx';
    link.click();
	}

	const marketingStore = useMarketingStore();

	const newLink = ref<ISignUpLink>({
		id: 0,
		name: '',
		fileUrl: '',
		created: Date.now(),
		lastUsed: 0,
		createdBy: '-',
		visited: 0,
		subscribers: 0,
	});

	const isFileUploaded = computed<boolean>(() => !!newLink.value.fileUrl?.length)

	const createLink = (): void => {
		if (!loading.value) {
			loading.value = true;
			marketingStore.createSignUpLink(newLink.value)
				.then((id) => {
					newLink.value.id = id;
					modalStore.closeModal();
					modalStore.openModal('MarketingNewLinkUpload');
				})
				.finally(() => {
					loading.value = false;
				})
		}
	};

	const fileLoading = ref<boolean>(false);
	async function uploadFile(file: File) {
			const storage = getStorage();
			const storageRefAlias = storageRef(storage, `marketingLinks/${'f-' + Date.now() + '.csv'}`);
			fileLoading.value = true;

			try {
				await uploadBytes(storageRefAlias, file);
				const downloadURL = await getDownloadURL(storageRefAlias);
				newLink.value.fileUrl = downloadURL;
				fileLoading.value = false;
			} catch (error) {
				console.error(error);
				fileLoading.value = false;
			}
	}

	const validateStep1 = () => {
		let isValid = true;

		if (!newLink.value.name?.length) {
			nameError.value = "Fill in the field";
			isValid = false;
		} else {
			nameError.value = "";
		}

		if (isValid) {
			download();
		}
	};

	const validateStep2 = () => {
		let isValid = true;

		if (!newLink.value.fileUrl?.length) {
			fileError.value = "Please upload a file.";
			isValid = false;
		} else {
			fileError.value = "";
		}
		if (isValid) {
			createLink();
		
		}
	};

	const base = import.meta.env.VITE_APP_URL;
</script>

<template>
	<div v-if="modalStore.isModalVisible('MarketingNewLink')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">link</span>
					<span>New Link</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<p class="modal__title">Let’s give this link a name</p>
				<div class="login__item">
					<input
						class="input"
						v-model="newLink.name"
						placeholder="Link Name*">
					<span class="material-symbols-outlined login__item_ico">link</span>
					<p class="error" v-if="nameError">{{ nameError }}</p>
				</div>
				<button type="button" class="login__btn" @click="validateStep1">Continue</button>
			</div>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('MarketingNewLinkDownload')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">link</span>
					<span>New Link</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<p class="modal__title modal__title-download">Upload Customer List</p>
				<p>
					To prevent unauthorized people, please use our template to upload a list of all users you are marketing to. This step is not skippable as people not on the list will not be able to register for a free FloodAlert device.
				</p>
				<input type="file" ref="fileInput" accept=".csv,.xlsx" style="display: none;" @change="handleFileChange" />
				<div class="bottom__wrap">
					<button v-if="!fileLoading" type="button" class=" upload__btn" @click="handleFileUpload">
						<span 
							class="material-symbols-outlined" 
							:class="{ 'uploaded-file': isFileUploaded }">
							{{ isFileUploaded ? 'task_alt' : 'upload' }}
						</span>
						<span :class="{ 'uploaded-file': isFileUploaded }">
							{{ isFileUploaded ? 'File Uploaded' : 'Upload File' }}
						</span>
					</button>
					<p v-if="!fileLoading && fileError" class="error error--two">{{ fileError }}</p>
					<p v-if="fileLoading"><br> loading...</p>
				</div>
				<div class="modal__btn modal__btn_download">
					<DefaultBtn v-if="!isFileUploaded" class="login__btn btn--white" :neutral="true" text="Download template" @click="upload" />
					<DefaultBtn :style="isFileUploaded ? 'margin: 0 auto;' : ''" class="login__btn" :loading text="Continue" @click="validateStep2" />
				</div>
			</div>
		</div>
	</div>
	
	<div v-if="modalStore.isModalVisible('MarketingNewLinkUpload')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">link</span>
					<span>New Link</span>
				</p>
				<button @click="modalStore.closeModal" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg class="modal__update_svg" width="93" height="94" viewBox="0 0 93 94" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M39.5532 57.505L28.1823 46.1341C27.5046 45.4564 26.6527 45.1096 25.6267 45.094C24.6007 45.0782 23.7331 45.425 23.024 46.1341C22.315 46.8432 21.9604 47.7029 21.9604 48.7132C21.9604 49.7234 22.315 50.5831 23.024 51.2922L36.4563 64.7245C37.3411 65.6093 38.3734 66.0517 39.5532 66.0517C40.733 66.0517 41.7652 65.6093 42.65 64.7245L69.8817 37.4928C70.5595 36.8151 70.9062 35.9633 70.9219 34.9373C70.9376 33.9112 70.5909 33.0437 69.8817 32.3346C69.1726 31.6255 68.313 31.271 67.3027 31.271C66.2924 31.271 65.4327 31.6255 64.7236 32.3346L39.5532 57.505ZM46.5082 93.5C40.0768 93.5 34.0316 92.2796 28.3727 89.8387C22.7136 87.3979 17.791 84.0854 13.605 79.9012C9.4188 75.7169 6.10477 70.7965 3.66286 65.1399C1.22096 59.4835 0 53.4396 0 47.0082C0 40.5768 1.22042 34.5316 3.66127 28.8727C6.10212 23.2136 9.41464 18.291 13.5988 14.105C17.7831 9.91881 22.7035 6.60478 28.3601 4.16287C34.0165 1.72096 40.0604 0.5 46.4918 0.5C52.9232 0.5 58.9684 1.72043 64.6273 4.16128C70.2864 6.60213 75.209 9.91464 79.395 14.0988C83.5812 18.2831 86.8952 23.2035 89.3371 28.8601C91.779 34.5165 93 40.5604 93 46.9918C93 53.4232 91.7796 59.4683 89.3387 65.1273C86.8979 70.7864 83.5854 75.709 79.4012 79.895C75.2169 84.0812 70.2965 87.3952 64.6399 89.8371C58.9835 92.279 52.9396 93.5 46.5082 93.5ZM46.5 86.158C57.4316 86.158 66.6908 82.3646 74.2777 74.7777C81.8646 67.1908 85.658 57.9316 85.658 47C85.658 36.0684 81.8646 26.8092 74.2777 19.2223C66.6908 11.6354 57.4316 7.842 46.5 7.842C35.5684 7.842 26.3092 11.6354 18.7223 19.2223C11.1354 26.8092 7.342 36.0684 7.342 47C7.342 57.9316 11.1354 67.1908 18.7223 74.7777C26.3092 82.3646 35.5684 86.158 46.5 86.158Z" fill="#2563EB"/>
				</svg>
				<p class="modal__update">Marketing link was created.</p>
				<p class="modal__update-text">To market to people not on the list, create a new marketing link.</p>
				<div class="copy__wrap">
					<CopyButton :text="`${base || ''}/registration?l=${newLink.id}`"/>
					<p class="copy modal__main-copy">{{ `${base || ''}/registration?l=${newLink.id}` }}</p>
				</div>
				
			</div>
		</div>
	</div>
</template>

<style scoped>

.modal__body{
	width: 600px;
}
.login__item_ico {
	top: 50%;
}

.modal__title{
	font-size: 22px;
	font-weight: 700;
	margin-bottom: 30px;
}

.modal__btn button{
	width: 50%;
}
.btn--white{
	background: #fff;
	border: 1px solid #E2E8F0;
	color: #0F172A;
}
.modal__btn{
	margin-top: 40px;
}

.modal__btn_download{
	margin-top: 30px;
}
.login__btn{
	box-shadow: none;
	min-width: 245px !important;
	font-weight: 500;
}
.upload__btn{
	margin: 30px auto 0 auto;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
	background: none;
	outline: none;
	transition: all .3s;
}

.upload__btn:hover{
	fill:#2563EB;
	color: #2563EB;
}

.error--two{
	left: 3px;
}

.bottom__wrap{
	position: relative;
}
.modal__update-text{
	margin-bottom: 30px;
}
.copy__wrap{
	display: flex;
	align-items: center;
	gap: 10px;
	margin: 0 auto; 
}
.modal__main-copy{
	font-size: 14px;
}

.uploaded-file {
  color: #34D399;
}
.uploaded-file:last-child {
  color: #2563EB;
}
.modal__title-download{
	margin-bottom: 12px;
}


</style>